import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Video, Users, Clock, HardDrive, Activity, TrendingUp, Play, Download, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal state for recordings list
  const [showRecordingsModal, setShowRecordingsModal] = useState(false);
  const [modalType, setModalType] = useState('all'); // 'all' or 'today'
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);

  // Video player modal
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/stats');
        setStats(res.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
      setLoading(false);
    };
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openRecordingsModal = async (type) => {
    setModalType(type);
    setShowRecordingsModal(true);
    setLoadingRecordings(true);

    try {
      let res;
      if (type === 'today') {
        res = await api.get('/recordings/today');
        setRecordings(res.data.recordings);
      } else {
        res = await api.get('/recordings?limit=50');
        setRecordings(res.data.recordings);
      }
    } catch (err) {
      toast.error('Failed to fetch recordings');
    }
    setLoadingRecordings(false);
  };

  const playVideo = (recording) => {
    if (!recording?.cloudUrl) {
      toast.error('Video file not available');
      return;
    }

    setSelectedVideo(recording);
    setVideoModal(true);
  };

  const downloadVideo = (recording) => {
    if (!recording?.cloudUrl) {
      toast.error('Video file not available');
      return;
    }

    const a = document.createElement('a');
    a.href = recording.cloudUrl;
    a.download = `${recording.trackingId}-${format(new Date(recording.createdAt), 'yyyy-MM-dd')}.webm`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    toast.success('Download started');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Recordings',
      value: stats?.totalRecordings || 0,
      icon: Video,
      color: 'indigo',
      subtitle: 'All time',
      clickable: true,
      onClick: () => openRecordingsModal('all')
    },
    {
      title: "Today's Recordings",
      value: stats?.todayRecordings || 0,
      icon: TrendingUp,
      color: 'green',
      subtitle: 'Last 24 hours',
      clickable: true,
      onClick: () => openRecordingsModal('today')
    },
    {
      title: 'Active Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'purple',
      subtitle: 'All roles',
      clickable: false
    },
    {
      title: 'Active Executives',
      value: stats?.activeExecutives || 0,
      icon: Activity,
      color: 'yellow',
      subtitle: 'Packing staff',
      clickable: false
    },
    {
      title: 'Storage Used',
      value: formatBytes(stats?.totalStorage),
      icon: HardDrive,
      color: 'cyan',
      subtitle: 'Video files',
      clickable: false
    }
  ];

  const colorClasses = {
    indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-zinc-400 mt-1">Overview of your video management system</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className={`bg-zinc-900/50 border-zinc-800 transition-all ${stat.clickable
              ? 'hover:border-indigo-500/50 hover:shadow-[0_0_15px_rgba(99,102,241,0.2)] cursor-pointer'
              : 'hover:border-zinc-700'
              }`}
            onClick={stat.onClick}
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${colorClasses[stat.color]}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                {stat.clickable && (
                  <span className="text-xs text-indigo-400 uppercase tracking-wider">Click to view</span>
                )}
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-sm font-medium text-zinc-300 mt-1">{stat.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{stat.subtitle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-400" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {stats?.recentLogs?.length > 0 ? (
                stats.recentLogs.map((log, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-zinc-950/50 rounded-lg border border-zinc-800/50">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${log.action.includes('START') ? 'bg-green-500' :
                      log.action.includes('STOP') ? 'bg-red-500' :
                        log.action.includes('LOGIN') ? 'bg-indigo-500' :
                          'bg-zinc-500'
                      }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-300">
                        <span className="font-medium text-white">{log.userName}</span>
                        {' '}{log.action.toLowerCase().replace(/_/g, ' ')}
                        {log.trackingId && <span className="text-indigo-400 font-mono ml-1">#{log.trackingId}</span>}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {new Date(log.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-zinc-500 text-center py-8">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-indigo-400" />
              Recording Status
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {stats?.byStatus?.length > 0 ? (
                stats.byStatus.map((status, index) => {
                  const statusColors = {
                    completed: { bg: 'bg-green-500', label: 'Completed' },
                    recording: { bg: 'bg-red-500', label: 'Recording' },
                    compressed: { bg: 'bg-blue-500', label: 'Compressed' },
                    uploaded: { bg: 'bg-purple-500', label: 'Uploaded' },
                    error: { bg: 'bg-yellow-500', label: 'Error' }
                  };
                  const config = statusColors[status._id] || { bg: 'bg-zinc-500', label: status._id };
                  const percentage = Math.round((status.count / stats.totalRecordings) * 100) || 0;

                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${config.bg}`} />
                          <span className="text-sm text-zinc-300">{config.label}</span>
                        </div>
                        <span className="text-sm font-mono text-zinc-400">{status.count}</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full ${config.bg} transition-all`} style={{ width: `${percentage}%` }} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-500 text-center py-8">No recordings yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recordings List Modal */}
      <Dialog open={showRecordingsModal} onOpenChange={setShowRecordingsModal}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden bg-zinc-900 border-zinc-800" data-testid="recordings-list-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-indigo-400" />
              {modalType === 'today' ? "Today's Recordings" : 'All Recordings'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[60vh]">
            {loadingRecordings ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full" />
              </div>
            ) : recordings.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No recordings found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Tracking ID</TableHead>
                    <TableHead className="text-zinc-400">Executive</TableHead>
                    <TableHead className="text-zinc-400">Date & Time</TableHead>
                    <TableHead className="text-zinc-400">Duration</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recordings.map((rec) => (
                    <TableRow key={rec.id} className="border-zinc-800">
                      <TableCell className="font-mono text-indigo-400">{rec.trackingId}</TableCell>
                      <TableCell className="text-zinc-300">{rec.executiveName}</TableCell>
                      <TableCell className="text-zinc-400">{format(new Date(rec.createdAt), 'PPp')}</TableCell>
                      <TableCell className="font-mono text-zinc-400">{formatDuration(rec.duration)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => playVideo(rec)}
                            disabled={!rec.cloudUrl}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                            data-testid={`modal-play-${rec.id}`}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadVideo(rec)}
                            disabled={!rec.cloudUrl}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                            data-testid={`modal-download-${rec.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" className="border-zinc-700" onClick={() => navigate('/search')}>
              View All in Search
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      <Dialog open={videoModal} onOpenChange={setVideoModal}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800" data-testid="video-player-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Recording: <span className="text-indigo-400 font-mono">{selectedVideo?.trackingId}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedVideo?.cloudUrl ? (
              <video
                src={selectedVideo.cloudUrl}
                controls
                className="w-full rounded-lg bg-black"
              />
            ) : (
              <p className="text-zinc-500">Video file not available</p>
            )}
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Executive</p>
                <p className="text-zinc-300">{selectedVideo?.executiveName}</p>
              </div>
              <div>
                <p className="text-zinc-500">Date</p>
                <p className="text-zinc-300">{selectedVideo && format(new Date(selectedVideo.createdAt), 'PPp')}</p>
              </div>
              <div>
                <p className="text-zinc-500">Duration</p>
                <p className="text-zinc-300 font-mono">{formatDuration(selectedVideo?.duration)}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => downloadVideo(selectedVideo)} className="bg-indigo-600 hover:bg-indigo-500">
                <Download className="w-4 h-4 mr-2" />
                Download Video
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
